import { createSlice } from '@reduxjs/toolkit';
import { AppState } from '../store';

export interface ISalesConfigurationState {
  playerAttributes: IPlayerAttribute[];
  salesSteerings: ISalesSteering[];
  salesSteeringRemarks: ISalesSteeringRemark[];
  bookingRestrictions: IBookingRestriction[];
  publications: IPublication[];
  searchedSalesSteerings: ISalesSteering[];
  salesSteeringsLoading: boolean;
  countries: ICountry[];
  regions: IRegion[];
  destinations: IDestination[];
  accommodations: IAccommodation[];
  transportHubs: ITransportHub[];
  geographies: IGeography[];
  accommodationBlockings: IAccommodationBlocking[];
  accommodationBlockingsLoading: boolean;
}

export interface IPlayerAttribute {
  accommodationCode: string;
  publicationCode: string;
  countryCode: string;
  regionCode: string;
  destinationCode: string;
  remark: string;
  isEnabled: boolean;
  externalId: number;
  createdDate: string;
  modifiedDate: string;
}

export interface ISalesSteering {
  id: number;
  accommodationCode: string;
  publicationCode: string;
  countryCode: string;
  regionCode: string;
  destinationCode: string;
  startDate: string;
  endDate: string;
  contractId: number;
  transportHubTypeName: string;
  transportHubs: string;
  createdDate: string;
  modifiedDate: string;
  isAttentionNeeded: boolean;
}

export interface ISalesSteeringRemark {
  remark: string;
  startDate: string;
  endDate: string;
  salesSteeringId: number;
  entityType: string;
  entityValue: string;
  type: string;
  isAttentionNeeded: boolean;
}

export interface IBookingRestriction {
  accommodationCode: string;
  contractId: string;
  startDate: string;
  endDate: string;
  status: number;
  remark: string;
  countryCode: string;
  regionCode: string;
  destinationCode: string;
  daysBeforeServiceStartsFrom: number;
  daysBeforeServiceStartsTill: number;
  bookability: number;
  createdDate: string;
  modifiedDate: string;
  publicationCodes: string[];
}

export interface IPublication {
  id: number;
  publicationCode: string;
  businessUnit: string | null;
  name: string;
}

export interface ICountry {
  code: string;
  name: string;
}

export interface IRegion {
  code: string;
  name: string;
}

export interface IDestination {
  code: string;
  name: string;
}

export interface IAccommodation {
  id: number;
  code: string;
  countryCode: string | null;
  regionCode: string | null;
  destinationCode: string | null;
}

export interface ITransportHub {
  id: number;
  code: string;
  name: string;
  type: 'Airport' | 'BusStop' | 'Other';
}

export interface IGeography {
  id: number;
  countryCode: string;
  regionCode: string;
  destinationCode: string;
}

export interface IIdCodePair {
  id: number;
  code: string;
}

export interface IAccommodationBlocking {
  id: number;
  accommodationBlockingKey: string;
  publications: IIdCodePair[];
  countryCodes: string[];
  regionCodes: string[];
  destinationCodes: string[];
  accommodations: IIdCodePair[];
  transportHubs: IIdCodePair[];
  startDate: string;
  endDate: string;
  transportDirection: 'Outbound' | 'Inbound' | null;
  salesSteeringType: 'CheckIn' | 'CheckOut' | 'Stay';
  remark: string;
  durations: string | null;
  isProcessed: boolean;
  createdBy: string;
  createdDate: string;
  modifiedBy: string | null;
  modifiedDate: string | null;
}

const initialState: ISalesConfigurationState = {
  playerAttributes: [],
  salesSteerings: [],
  salesSteeringRemarks: [],
  bookingRestrictions: [],
  publications: [],
  searchedSalesSteerings: [],
  salesSteeringsLoading: false,
  countries: [],
  regions: [],
  destinations: [],
  accommodations: [],
  transportHubs: [],
  geographies: [],
  accommodationBlockings: [],
  accommodationBlockingsLoading: false,
};

export const salesConfiguration = createSlice({
  name: 'salesConfiguration',
  initialState,
  reducers: {
    setPlayerAttributes(state, action) {
      state.playerAttributes = action.payload;
    },
    setSalesSteerings(state, action) {
      state.salesSteerings = action.payload;
    },
    setSearchedSalesSteerings(state, action) {
      state.searchedSalesSteerings = action.payload;
    },
    setSalesSteeringRemarks(state, action) {
      if (!state.salesSteeringRemarks.some((ssr) => ssr.salesSteeringId == action.payload[0].salesSteeringId)) {
        state.salesSteeringRemarks = [...state.salesSteeringRemarks, ...action.payload];
      }
    },
    setBookingRestrictions(state, action) {
      state.bookingRestrictions = action.payload;
    },
    setPublications(state, action) {
      state.publications = action.payload;
    },
    setSalesSteeringsLoading(state, action) {
      state.salesSteeringsLoading = action.payload;
    },
    setCountries(state, action) {
      state.countries = action.payload;
    },
    setRegions(state, action) {
      state.regions = action.payload;
    },
    setDestinations(state, action) {
      state.destinations = action.payload;
    },
    setAccommodations(state, action) {
      state.accommodations = action.payload;
    },
    setTransportHubs(state, action) {
      state.transportHubs = action.payload;
    },
    setGeographies(state, action) {
      state.geographies = action.payload;
    },
    setAccommodationBlockings(state, action) {
      state.accommodationBlockings = action.payload;
    },
    setAccommodationBlockingsLoading(state, action) {
      state.accommodationBlockingsLoading = action.payload;
    },
  },
});

export const { setPlayerAttributes } = salesConfiguration.actions;
export const selectPlayerAttributes = (state: AppState) => state.salesConfiguration.playerAttributes;

export const { setSalesSteerings } = salesConfiguration.actions;
export const selectSalesSteerings = (state: AppState) => state.salesConfiguration.salesSteerings;

export const { setSearchedSalesSteerings } = salesConfiguration.actions;
export const selectSearchedSalesSteerings = (state: AppState) => state.salesConfiguration.searchedSalesSteerings;

export const { setSalesSteeringRemarks } = salesConfiguration.actions;
export const selectSalesSteeringRemarks = (state: AppState) => state.salesConfiguration.salesSteeringRemarks;

export const { setBookingRestrictions } = salesConfiguration.actions;
export const selectBookingRestrictions = (state: AppState) => state.salesConfiguration.bookingRestrictions;

export const { setPublications } = salesConfiguration.actions;
export const selectPublications = (state: AppState) => state.salesConfiguration.publications;

export const { setSalesSteeringsLoading } = salesConfiguration.actions;
export const selectSalesSteeringsLoading = (state: AppState) => state.salesConfiguration.salesSteeringsLoading;

export const { setCountries } = salesConfiguration.actions;
export const selectCountries = (state: AppState) => state.salesConfiguration.countries;

export const { setRegions } = salesConfiguration.actions;
export const selectRegions = (state: AppState) => state.salesConfiguration.regions;

export const { setDestinations } = salesConfiguration.actions;
export const selectDestinations = (state: AppState) => state.salesConfiguration.destinations;

export const { setAccommodations } = salesConfiguration.actions;
export const selectAccommodations = (state: AppState) => state.salesConfiguration.accommodations;

export const { setTransportHubs } = salesConfiguration.actions;
export const selectTransportHubs = (state: AppState) => state.salesConfiguration.transportHubs;

export const { setGeographies } = salesConfiguration.actions;
export const selectGeographies = (state: AppState) => state.salesConfiguration.geographies;

export const { setAccommodationBlockings } = salesConfiguration.actions;
export const selectAccommodationBlockings = (state: AppState) => state.salesConfiguration.accommodationBlockings;

export const { setAccommodationBlockingsLoading } = salesConfiguration.actions;
export const selectAccommodationBlockingsLoading = (state: AppState) =>
  state.salesConfiguration.accommodationBlockingsLoading;

export default salesConfiguration.reducer;
